// src/pages/business/notifications/NotificationsPage.tsx
// Notifications Center - Centralized notification management

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Bell, Check, CheckCheck, Trash2, Filter, Search, X,
  AlertCircle, Info, CheckCircle, AlertTriangle, Package,
  ShoppingCart, MessageSquare, DollarSign, Users, TrendingUp,
  Clock, ChevronRight, Loader2
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';
interface Notification {
  id: string;
  type: 'order' | 'payment' | 'inventory' | 'customer' | 'system' | 'marketing' | 'booking';
  title: string;
  message: string;
  data?: any;
  created_at: string;
  read: boolean;
  action_url?: string;
}

interface NotificationStats {
  total: number;
  unread: number;
  today: number;
  thisWeek: number;
}

type FilterType = 'all' | 'unread' | 'orders' | 'payments' | 'inventory' | 'customers' | 'marketing';

const notificationTypeConfig: Record<string, { label: string; icon: React.ElementType; color: string; bgColor: string }> = {
  order: { label: 'Orders', icon: ShoppingCart, color: '#3B82F6', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  payment: { label: 'Payments', icon: DollarSign, color: '#10B981', bgColor: 'bg-green-100 dark:bg-green-900/30' },
  inventory: { label: 'Inventory', icon: Package, color: '#F59E0B', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
  customer: { label: 'Customers', icon: Users, color: '#8B5CF6', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
  system: { label: 'System', icon: Info, color: '#6B7280', bgColor: 'bg-gray-100 dark:bg-gray-900/30' },
  marketing: { label: 'Marketing', icon: TrendingUp, color: '#EC4899', bgColor: 'bg-pink-100 dark:bg-pink-900/30' },
  booking: { label: 'Bookings', icon: Clock, color: '#0EA5E9', bgColor: 'bg-cyan-100 dark:bg-cyan-900/30' },
};

const NotificationsPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<NotificationStats>({ total: 0, unread: 0, today: 0, thisWeek: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [selectedNotifications, setSelectedNotifications] = useState<Set<string>>(new Set());
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, [businessId]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/businesses/${businessId}/notifications`);
      const data = res.data.notifications || [];
      setNotifications(data);
      
      // Calculate stats
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      setStats({
        total: data.length,
        unread: data.filter((n: Notification) => !n.read).length,
        today: data.filter((n: Notification) => new Date(n.created_at) >= today).length,
        thisWeek: data.filter((n: Notification) => new Date(n.created_at) >= weekAgo).length,
      });
    } catch (error: any) {
      console.error('Failed to fetch notifications:', error);
      toastError('Failed to load notifications');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await apiClient.patch(`/businesses/${businessId}/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
    } catch (error: any) {
      toastError('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.post(`/businesses/${businessId}/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setStats(prev => ({ ...prev, unread: 0 }));
      toastSuccess('All notifications marked as read');
    } catch (error: any) {
      toastError('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      await apiClient.delete(`/businesses/${businessId}/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setStats(prev => ({ ...prev, total: prev.total - 1 }));
      toastSuccess('Notification deleted');
    } catch (error: any) {
      toastError('Failed to delete notification');
    }
  };

  const deleteSelected = async () => {
    if (selectedNotifications.size === 0) return;
    
    try {
      await apiClient.post(`/businesses/${businessId}/notifications/delete-batch`, {
        ids: Array.from(selectedNotifications)
      });
      setNotifications(prev => prev.filter(n => !selectedNotifications.has(n.id)));
      setSelectedNotifications(new Set());
      toastSuccess(`${selectedNotifications.size} notifications deleted`);
    } catch (error: any) {
      toastError('Failed to delete notifications');
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Navigate based on notification type and data
    if (notification.action_url) {
      navigate(notification.action_url);
    } else {
      // Default navigation based on type
      switch (notification.type) {
        case 'order':
          if (notification.data?.orderId) {
            navigate(`/business/${businessId}/orders/${notification.data.orderId}`);
          }
          break;
        case 'payment':
          navigate(`/business/${businessId}/payments`);
          break;
        case 'inventory':
          navigate(`/business/${businessId}/inventory`);
          break;
        case 'customer':
          navigate(`/business/${businessId}/reviews`);
          break;
        case 'booking':
          if (notification.data?.bookingId) {
            navigate(`/business/${businessId}/bookings/${notification.data.bookingId}`);
          }
          break;
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const isToday = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.getDate() === today.getDate() && 
           date.getMonth() === today.getMonth() && 
           date.getFullYear() === today.getFullYear();
  };

  const filteredNotifications = notifications.filter(n => {
    // Apply type filter
    if (activeFilter !== 'all' && activeFilter !== 'unread') {
      if (n.type !== activeFilter) return false;
    }
    if (activeFilter === 'unread' && n.read) return false;
    
    // Apply search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return n.title.toLowerCase().includes(q) || 
             n.message.toLowerCase().includes(q);
    }
    
    return true;
  });

  const toggleSelectNotification = (id: string) => {
    setSelectedNotifications(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedNotifications.size === filteredNotifications.length) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(new Set(filteredNotifications.map(n => n.id)));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
            <Bell size={24} className="text-primary" />
          </button>
          <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
            Notifications
          </h1>
          {stats.unread > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-primary hover:bg-primary/10 transition-colors"
            >
              <CheckCheck size={14} />
              Mark all read
            </button>
          )}
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-light-text dark:text-dark-text">{stats.unread}</p>
            <p className="text-[10px] text-light-subtle dark:text-dark-subtle">Unread</p>
          </div>
          <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-light-text dark:text-dark-text">{stats.today}</p>
            <p className="text-[10px] text-light-subtle dark:text-dark-subtle">Today</p>
          </div>
          <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-light-text dark:text-dark-text">{stats.thisWeek}</p>
            <p className="text-[10px] text-light-subtle dark:text-dark-subtle">This Week</p>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
            <input
              type="text"
              placeholder="Search notifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-9 py-2 rounded-lg bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X size={14} className="text-light-subtle dark:text-dark-subtle" />
              </button>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              className="p-2 rounded-lg border border-light-border dark:border-dark-border hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              <Filter size={18} className="text-light-text dark:text-dark-text" />
            </button>
            {showFilterMenu && (
              <div className="absolute right-0 top-full mt-2 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg shadow-lg z-50 min-w-[160px]">
                <div className="p-2">
                  {(['all', 'unread', 'orders', 'payments', 'inventory', 'customers', 'marketing'] as FilterType[]).map(filter => (
                    <button
                      key={filter}
                      onClick={() => { setActiveFilter(filter); setShowFilterMenu(false); }}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        activeFilter === filter
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg'
                      }`}
                    >
                      {filter === 'all' ? 'All Notifications' : 
                       filter === 'unread' ? 'Unread Only' :
                       notificationTypeConfig[filter]?.label || filter}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bulk actions */}
        {selectedNotifications.size > 0 && (
          <div className="flex items-center gap-2 mt-2 p-2 bg-primary/10 rounded-lg">
            <span className="text-xs font-semibold text-primary">
              {selectedNotifications.size} selected
            </span>
            <button
              onClick={() => {
                // Mark selected as read
                selectedNotifications.forEach(id => markAsRead(id));
                setSelectedNotifications(new Set());
              }}
              className="px-3 py-1 rounded-md text-xs font-semibold text-primary hover:bg-primary/20 transition-colors"
            >
              Mark read
            </button>
            <button
              onClick={deleteSelected}
              className="px-3 py-1 rounded-md text-xs font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              Delete
            </button>
            <button
              onClick={() => setSelectedNotifications(new Set())}
              className="ml-auto px-3 py-1 rounded-md text-xs text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Notifications List */}
      <div className="max-w-5xl mx-auto px-4 pt-4">
        {filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
            <Bell size={64} className="text-light-subtle dark:text-dark-subtle" />
            <p className="font-semibold text-light-text dark:text-dark-text" style={{ fontSize: 18 }}>
              {searchQuery ? 'No matching notifications' : 'No notifications yet'}
            </p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle">
              {searchQuery ? 'Try adjusting your search or filters' : "You're all caught up! New notifications will appear here."}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredNotifications.map((notification) => {
              const config = notificationTypeConfig[notification.type] || notificationTypeConfig.system;
              const TypeIcon = config.icon;
              
              return (
                <div
                  key={notification.id}
                  className={`relative bg-light-card dark:bg-dark-card rounded-xl p-4 border transition-all ${
                    notification.read 
                      ? 'border-light-border dark:border-dark-border' 
                      : 'border-l-4 border-l-primary border-y-light-border border-r-light-border dark:border-y-dark-border dark:border-r-dark-border'
                  } ${selectedNotifications.has(notification.id) ? 'ring-2 ring-primary/30' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox for selection */}
                    <input
                      type="checkbox"
                      checked={selectedNotifications.has(notification.id)}
                      onChange={() => toggleSelectNotification(notification.id)}
                      className="mt-1 w-4 h-4 rounded border-light-border dark:border-dark-border text-primary focus:ring-primary/30"
                    />
                    
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${config.bgColor}`}>
                      <TypeIcon size={20} style={{ color: config.color }} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className={`font-semibold text-sm text-light-text dark:text-dark-text ${!notification.read ? 'font-bold' : ''}`}>
                            {notification.title}
                          </p>
                          <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className="text-[10px] text-light-subtle dark:text-dark-subtle">
                            {formatDate(notification.created_at)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-3">
                        {!notification.read && (
                          <button
                            onClick={() => markAsRead(notification.id)}
                            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
                          >
                            <Check size={12} />
                            Mark as read
                          </button>
                        )}
                        <button
                          onClick={() => handleNotificationClick(notification)}
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                        >
                          View
                          <ChevronRight size={12} />
                        </button>
                        <button
                          onClick={() => deleteNotification(notification.id)}
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ml-auto"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <button
        onClick={() => { setRefreshing(true); fetchNotifications(); }}
        disabled={refreshing}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg z-50"
        style={{ backgroundColor: '#4F46E5' }}
      >
        <Loader2 size={24} className={`text-white ${refreshing ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};

export default NotificationsPage;